import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Calculator,
  DollarSign,
  Users,
  MapPin,
  Info,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Home,
  Calendar,
  HelpCircle,
  RefreshCw,
  Loader2,
  ExternalLink
} from 'lucide-react';
import api from '../lib/api';
import { translateTemplate } from '../lib/publicListingTruth';

export default function TLACalculatorPage() {
  const { t, locale } = useLanguage();
  
  const [tlaRates, setTlaRates] = useState([]);
  const [ratesByCountry, setRatesByCountry] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    location: '',
    familySize: 'single', // 'single' or 'family'
    dependents: 0,
    daysNeeded: 60,
    propertyRent: '',
  });
  
  const [result, setResult] = useState(null);

  // Fetch TLA rates from backend
  const fetchTlaRates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/tla-rates');
      setTlaRates(response.data.rates || []);
      setRatesByCountry(response.data.by_country || {});
      setLastUpdated(response.data.last_updated);
    } catch (err) {
      console.error('Failed to fetch TLA rates:', err);
      setError('load_failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTlaRates();
  }, []);

  const calculateTLA = () => {
    if (!formData.location || tlaRates.length === 0) return;
    
    const locationData = tlaRates.find(r => r.location_key === formData.location);
    if (!locationData) return;
    
    const dailyRate = formData.familySize === 'family' 
      ? locationData.daily_family 
      : locationData.daily_single;
    
    // Standard TLA is up to 60 days (can be extended in special cases)
    const days = Math.min(formData.daysNeeded, 60);
    const totalTLA = dailyRate * days;
    const monthlyEquivalent = dailyRate * 30;
    
    // Compare with property rent if provided
    const propertyRent = parseInt(formData.propertyRent) || 0;
    const withinBudget = propertyRent <= monthlyEquivalent;
    const difference = monthlyEquivalent - propertyRent;
    
    setResult({
      locationName: locationData.location_name,
      country: locationData.country,
      dailyRate,
      days,
      totalTLA,
      monthlyEquivalent,
      propertyRent,
      withinBudget,
      difference,
      maxDays: 60,
      source: locationData.source,
      effectiveDate: locationData.effective_date,
    });
  };

  useEffect(() => {
    if (formData.location && tlaRates.length > 0) {
      calculateTLA();
    }
  }, [formData, tlaRates]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-military-gold/20 rounded-2xl mb-4">
            <Calculator className="w-8 h-8 text-military-gold" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            {t('tla.title', 'TLA Calculator')}
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            {t('tla.subtitle', 'Estimate your Temporary Lodging Allowance (TLA) for PCS moves to Europe. Compare rates with available properties.')}
          </p>
          <p className="text-xs text-slate-500 max-w-2xl mx-auto mt-3" data-testid="tla-non-affiliation">
            {t(
              'tla.non_affiliation',
              'Unofficial estimate only. TLA Housing Europe is an independent platform and is not affiliated with or endorsed by the U.S. Government or Department of Defense. Your actual TLA entitlement is determined solely by your command or housing office.',
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-signal-blue" />
              {t('tla.your_assignment', 'Your Assignment')}
            </h2>

            <div className="space-y-5">
              {/* Loading State */}
              {loading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-signal-blue animate-spin mr-2" />
                  <span className="text-slate-600">{t('tla.loading_rates', 'Loading TLA rates...')}</span>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {t(`tla.error.${error}`, 'Failed to load TLA rates. Please try again.')}
                  <Button variant="ghost" size="sm" onClick={fetchTlaRates} className="ml-auto" title={t('tla.retry', 'Retry')}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Rates published but empty — honest state instead of a dead dropdown */}
              {!loading && !error && Object.keys(ratesByCountry).length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800 text-sm flex items-start gap-2" data-testid="tla-rates-empty">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {t('tla.rates_empty', 'TLA rates are being updated and are temporarily unavailable. Please check back soon, or browse listings in the meantime.')}
                  </span>
                </div>
              )}

              {/* Location */}
              {!loading && !error && Object.keys(ratesByCountry).length > 0 && (
                <>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-700">{t('tla.duty_station', 'Duty Station / Location')}</Label>
                      {lastUpdated && (
                        <span className="text-xs text-slate-400">
                          {t('tla.updated_label', 'Updated:')} {new Date(lastUpdated).toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-US')}
                        </span>
                      )}
                    </div>
                    <Select
                      value={formData.location}
                      onValueChange={(v) => setFormData(prev => ({ ...prev, location: v }))}
                    >
                      <SelectTrigger className="mt-1 bg-white border-slate-300 text-slate-800" data-testid="tla-location">
                        <SelectValue placeholder={t('tla.select_location', 'Select your duty station')} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-slate-300 max-h-80">
                        {Object.entries(ratesByCountry).map(([country, locations]) => (
                          <div key={country}>
                            <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                              {country}
                            </div>
                            {locations.map((loc) => (
                              <SelectItem key={loc.location_key} value={loc.location_key} className="text-slate-700">
                                {loc.location_name}
                              </SelectItem>
                            ))}
                          </div>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

              {/* Family Size */}
              <div>
                <Label className="text-slate-700">{t('tla.family_status', 'Family Status')}</Label>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, familySize: 'single', dependents: 0 }))}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.familySize === 'single'
                        ? 'border-signal-blue bg-signal-blue/5 text-signal-blue'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid="tla-single"
                  >
                    <Users className="w-4 h-4" />
                    {t('tla.single', 'Single / Unaccompanied')}
                  </button>
                  <button
                    onClick={() => setFormData(prev => ({ ...prev, familySize: 'family', dependents: 1 }))}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.familySize === 'family'
                        ? 'border-signal-blue bg-signal-blue/5 text-signal-blue'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    data-testid="tla-family"
                  >
                    <Users className="w-4 h-4" />
                    {t('tla.with_family', 'With Family')}
                  </button>
                </div>
              </div>

              {/* Days Needed */}
              <div>
                <Label className="text-slate-700">{t('tla.days_needed', 'Days of TLA Needed')}</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    value={formData.daysNeeded}
                    onChange={(e) => setFormData(prev => ({ ...prev, daysNeeded: parseInt(e.target.value) || 0 }))}
                    min="1"
                    max="60"
                    className="pl-10 bg-white border-slate-300 text-slate-800"
                    data-testid="tla-days"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {t('tla.max_days', 'Standard TLA is up to 60 days (extensions possible with approval)')}
                </p>
              </div>

              {/* Property Rent (optional) */}
              <div>
                <Label className="text-slate-700">{t('tla.compare_property', 'Compare with Property Rent (Optional)')}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                  <Input
                    type="number"
                    value={formData.propertyRent}
                    onChange={(e) => setFormData(prev => ({ ...prev, propertyRent: e.target.value }))}
                    placeholder={t('tla.rent_placeholder', 'Enter monthly rent')}
                    className="pl-8 bg-white border-slate-300 text-slate-800"
                    data-testid="tla-rent"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {t('tla.compare_desc', 'Enter a property\'s monthly rent to see if it fits your TLA budget')}
                </p>
              </div>
                </>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="space-y-6">
            {result ? (
              <>
                {/* TLA Estimate Card */}
                <div className="bg-gradient-to-br from-military-gold/10 to-military-gold/5 rounded-2xl border border-military-gold/30 p-6">
                  <h3 className="font-heading text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-military-gold" />
                    {t('tla.your_estimate', 'Your TLA Estimate')}
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-military-gold/20">
                      <span className="text-slate-600">{t('tla.location', 'Location')}</span>
                      <span className="font-medium text-slate-800">{result.locationName}, {result.country}</span>
                    </div>

                    <div className="flex justify-between items-center pb-3 border-b border-military-gold/20">
                      <span className="text-slate-600">{t('tla.daily_rate', 'Daily Rate')}</span>
                      <span className="font-bold text-xl text-military-gold">€{result.dailyRate}</span>
                    </div>

                    <div className="flex justify-between items-center pb-3 border-b border-military-gold/20">
                      <span className="text-slate-600">{t('tla.monthly_equiv', 'Monthly Equivalent')}</span>
                      <span className="font-bold text-xl text-signal-blue">€{result.monthlyEquivalent.toLocaleString()}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-slate-600">
                        {translateTemplate(t('tla.total_with_days', 'Total TLA ({days} days)'), { days: result.days })}
                      </span>
                      <span className="font-bold text-2xl text-slate-800">€{result.totalTLA.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Budget Comparison (if property rent provided) */}
                {result.propertyRent > 0 && (
                  <div className={`rounded-2xl border p-6 ${
                    result.withinBudget 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {result.withinBudget ? (
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5" />
                      )}
                      <div>
                        <h4 className={`font-bold ${result.withinBudget ? 'text-emerald-700' : 'text-amber-700'}`}>
                          {result.withinBudget 
                            ? t('tla.within_budget', 'Within TLA Budget!') 
                            : t('tla.over_budget', 'Over TLA Budget')
                          }
                        </h4>
                        <p className={`text-sm mt-1 ${result.withinBudget ? 'text-emerald-600' : 'text-amber-600'}`}>
                          {result.withinBudget
                            ? translateTemplate(
                                t(
                                  'tla.budget_within_detail',
                                  'This property at €{rent}/mo is €{difference} under your TLA rate.',
                                ),
                                { rent: result.propertyRent, difference: result.difference },
                              )
                            : translateTemplate(
                                t(
                                  'tla.budget_over_detail',
                                  'This property at €{rent}/mo is €{amount} over your TLA rate.',
                                ),
                                { rent: result.propertyRent, amount: Math.abs(result.difference) },
                              )}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Browse Properties CTA */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                  <h4 className="font-heading font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <Home className="w-5 h-5 text-signal-blue" />
                    {t('tla.find_properties', 'Find Properties in Your Budget')}
                  </h4>
                  <p className="text-sm text-slate-600 mb-4">
                    {translateTemplate(
                      t(
                        'tla.browse_desc',
                        'Browse TLA-approved properties under €{max}/month in {location}.',
                      ),
                      {
                        max: result.monthlyEquivalent.toLocaleString(),
                        location: result.locationName,
                      },
                    )}
                  </p>
                  <Button 
                    className="w-full bg-signal-blue hover:bg-signal-blue-hover"
                    onClick={() => window.location.href = `/listings`}
                  >
                    {t('tla.browse_now', 'Browse Properties')}
                  </Button>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-slate-600 mb-2">
                  {t('tla.empty_title', 'Select Your Location')}
                </h3>
                <p className="text-sm text-slate-500">
                  {t('tla.empty_desc', 'Choose your duty station to see your estimated TLA rates.')}
                </p>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-slate-500 mt-0.5" />
                <div className="text-sm text-slate-600">
                  <p className="font-medium text-slate-700 mb-1">{t('tla.disclaimer_title', 'Important Note')}</p>
                  <p>{t('tla.disclaimer', 'TLA rates are estimates based on DoD per diem rates and may vary. Actual rates depend on your orders, rank, and specific circumstances. Always verify with your finance office.')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
